import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BRAND, IMAGES } from "../lib/site";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.35 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden">
    <motion.span variants={line} custom={i} initial="hidden" animate="show" className="block">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  // 3-layer parallax · Obsidian Grid (background · slowest) · India /
  // Skyline mid-layer (medium) · Foreground text (fastest). Different
  // speeds give the hero a "3D window" depth without a video load.
  const gridY   = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const midY    = useTransform(scrollYProgress, [0, 1], ["0%", "26%"]);
  const midScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fgY     = useTransform(scrollYProgress, [0, 1], ["0%", "46%"]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[680px] w-full overflow-hidden">
      {/* LAYER 1 · Obsidian grid backdrop — slowest, feels "far" */}
      <motion.div style={{ y: gridY }} className="absolute inset-0 -top-24 pointer-events-none" aria-hidden>
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-[0.42]"
          src="/assets/obsidian-grid.webm"
          poster="/assets/obsidian-grid-poster.png"
          autoPlay muted loop playsInline preload="auto"
        />
      </motion.div>
      {/* LAYER 2 · Skyline / India context — mid speed, primary photograph */}
      <motion.div style={{ y: midY, scale: midScale }} className="absolute inset-0" data-testid="hero-parallax-mid">
        <img src={IMAGES.heroPrimary} alt="Skyline under construction at dusk" className="w-full h-full object-cover mix-blend-luminosity opacity-90" />
      </motion.div>
      {/* Obsidian tint · keeps the foreground text legible */}
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-black" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/50" />

      {/* LAYER 3 · Foreground text — moves fastest, creates the 3D-window feel */}
      <motion.div style={{ y: fgY }} className="relative z-10 h-full max-w-[1600px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-16 md:pb-24" data-testid="hero-parallax-fg">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="w-10 h-px bg-[#B87333]" />
          <span className="overline text-[#B87333]">{BRAND.motto}</span>
        </motion.div>

        <h1 className="font-serif text-[#F5F5F0] leading-[0.92] tracking-[-0.02em] text-[clamp(2.8rem,9vw,8.5rem)]">
          <MaskLine i={0}>Building the</MaskLine>
          <MaskLine i={1}>
            world in <span className="italic text-[#B87333]">bulk.</span>
          </MaskLine>
        </h1>

        <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="max-w-md font-body text-base md:text-lg text-[#C9C9C2] leading-relaxed"
          >
            {BRAND.tagline}
          </motion.p>
          <motion.button
            data-testid="hero-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.9 }}
            onClick={() => document.querySelector("#materials")?.scrollIntoView({ behavior: "smooth" })}
            className="group inline-flex items-center gap-4 self-start rounded-full bg-[#F5F5F0] text-black pl-7 pr-3 py-3 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors duration-300"
          >
            Explore Materials
            <span className="grid place-items-center w-9 h-9 rounded-full bg-black text-[#F5F5F0] group-hover:rotate-45 transition-transform duration-300">↗</span>
          </motion.button>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-6 right-6 md:right-12 z-10 flex items-center gap-3"
      >
        <span className="overline text-[#8A8A82]">Scroll</span>
        <span className="block w-px h-10 bg-white/20 relative overflow-hidden">
          <motion.span
            animate={{ y: ["-100%", "100%"] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
            className="absolute inset-x-0 top-0 h-1/2 bg-[#B87333]"
          />
        </span>
      </motion.div>
    </section>
  );
};
